<template>
  <v-popover
    interactive
    tag="div"
    content-tag="div"
    :append-to="bodyRef"
    :role="'poper'"
    :trigger="'click'"
    @state="onDropdownStateChange"
  >
    <template #default="{ state }">
      <div
        class="board-list-actions group"
        :class="{ flex: state.isVisible }"
      >
        <em
          class="flex custom-icons"
          :style="getCustomIconStyle(iconPath('more-horizontal'))"
        />
      </div>
    </template>
    <template #content="{ state, hide }">
      <ListDropdown
        v-if="state.isVisible"
        class="dropdown-position board-list-options"
        position=""
        :items="settingsDropdownItems"
        @click="handleSettingsDropdownClick($event, hide)"
        @closeEscape="hideEditDropDown(hide)"
      />
    </template>
  </v-popover>
</template>

<script>
import ListDropdown from '@/components/widgets/ListDropdown';
import EventBus from '@/utilities/eventBus';
import customIconsMixin from '@/mixins/customIconsMixin';
import watchMixin from '@/mixins/watchMixin';
import { groupByOption } from '@/types/groupByOption';
import { ResourceType } from '@/types/resources';
import useBoardEmbed from '@/boards/composables/useBoardEmbed';

export default {
  name: 'BoardListActions',
  components: {
    ListDropdown,
  },

  mixins: [customIconsMixin, watchMixin],

  props: {
    list: {
      type: Object,
      required: true,
    },
  },

  emits: ['renameList'],

  setup() {
    const { embed, embeddedBoardAttributes } = useBoardEmbed();

    return {
      embeddedBoardAttributes,
      embed,
      bodyRef: document.body,
    };
  },

  computed: {
    currentBoardId() {
      return this.$store.getters.currentBoardId;
    },
    isEpicsBoard() {
      return this.list.board_id === this.$constants.epicsBoardId;
    },
    isListForEdit() {
      return this.$store.getters.getCurrentListIdActionEdit === this.list.id;
    },
    settingsDropdownItems() {
      const items = [
        {
          id: 'addCard',
          label: this.translate(this.isEpicsBoard ? 'addProject' : 'addCard'),
          customIconName: this.iconPath('plus'),
        },
      ];

      if (this.boardGroupBy === groupByOption.status && !this.isSprint) {
        items.push(
          {
            id: 'renameList',
            label: this.translate('editList'),
            customIconName: this.iconPath('edit'),
          },
          {
            id: 'watchList',
            label: this.watchText,
            separator: true,
            customIconName: this.watchIcon,
          },
          // archiveList is commented out untile we add a way to browse archived things
          // {
          //   id: 'archiveList',
          //   label: this.translate('archiveList'),
          //   customIconName: this.iconPath('archived'),
          // },
          {
            id: 'deleteList',
            label: this.translate('deleteList'),
            customIconName: this.iconPath('trash'),
            deleteOrCancelType: true,
          }
        );
      }
      return items;
    },

    currentBoardTitle() {
      return this.$store.getters.currentBoard.title;
    },
    boardGroupBy() {
      if (this.embed) {
        return this.embeddedBoardAttributes?.groupBy;
      }
      return this.$store.getters.getBoardGroupBy(this.currentBoardId);
    },
    isWatching() {
      return this.list.is_watching;
    },
    watchIcon() {
      return this.iconPath(this.isWatching ? 'bell-off' : 'bell');
    },
    watchText() {
      return this.translate(this.isWatching ? 'unsubscribe' : 'subscribe');
    },
    isSprint() {
      return this.$route.name === this.$constants.routeNames.sprint;
    },
  },
  methods: {
    // archiveList() {
    //   this.$store.dispatch('archiveList', this.list.id);
    // },
    renameList() {
      this.$emit('renameList');
    },
    addCard() {
      this.$store.dispatch('setListDraftCardCreateView', this.list.id);
      EventBus.$emit('focusDraftCardInput', { listId: this.list.id });
    },
    onDropdownStateChange(state) {
      if (!state.isVisible) {
        this.$store.dispatch('setCurrentListIdForActionEdit', '');
      } else {
        this.$store.dispatch('setCurrentListIdForActionEdit', this.list.id);
      }
    },
    handleSettingsDropdownClick(item, hideFn) {
      if (typeof hideFn === 'function') hideFn();
      switch (item.id) {
        case 'addCard':
          this.addCard();
          break;
        case 'renameList':
          this.renameList();
          break;
        case 'deleteList':
          this.deleteList();
          break;
        case 'watchList':
          this.watchList();
          break;
        default:
          break;
      }
    },
    hideEditDropDown(hideFn) {
      if (typeof hideFn === 'function') hideFn();
    },

    watchList() {
      this.toggleWatch(this.isWatching, ResourceType.List, this.list.id);
    },

    deleteList() {
      this.$store.dispatch('deletePopupId', `${ResourceType.List}-${this.list.id}`);
    },
  },
};
</script>

<style lang="scss">
.board-list-actions {
  display: flex;
  position: relative;
  align-items: center;
  justify-content: center;
  @apply h-6 w-6 rounded;

  &:hover {
    @apply bg-icon-bg;

    em {
      @apply bg-icon-hover;
    }
  }

  .v-popover {
    @apply w-fit;

    .trigger {
      display: flex !important;
      @apply h-6 w-6 items-center justify-center;
    }
  }
}
</style>
